import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  loginRequest,
  meRequest,
  refreshRequest,
  logoutRequest,
  mfaChallengeRequest,
} from '../api/authApi';
import {
  clearAuthSession,
  getAuthToken,
  getRefreshToken,
  saveAuthSession,
} from '../utils/auth';
import { setSentryUser, clearSentryUser } from '../utils/sentry';

/**
 * AuthContext — global authentication state for Medtransact.
 *
 * Initialization sequence (runs once on mount):
 *   1. Read stored access token
 *   2. Call /auth/me with that token
 *   3. On fail → try /auth/refresh → retry /auth/me
 *   4. On fail → clearAuthSession(), user remains null
 *
 * Login flow (Architecture Rules 38 + 43):
 *   1. login(email, password, rememberMe) calls /auth/login
 *   2. If backend returns mfa_required → context stages the challenge token
 *      and login() resolves with { status: 'mfa_required', methods, expiresIn }
 *      so the SPA can render the OTP form
 *   3. submitMfaChallenge(code, codeType) POSTs /auth/mfa/challenge with the
 *      staged token and completes the session
 *
 * Downstream:
 *   - useAuth() hook — reads context values
 *   - useScopePermissions() hook — derives scope checks from user.scopes
 */

const AuthContext = createContext(null);

/**
 * The backend envelope `data` shape from /auth/login + /auth/mfa/challenge is
 * either:
 *   - { access_token, refresh_token, token_type, expires_in, user, ... }
 *   - { mfa_required: true, mfa_challenge_token, mfa_methods, expires_in }
 *
 * This helper normalizes the success shape to what saveAuthSession() expects.
 */
function readTokenEnvelope(data) {
  return {
    auth: {
      access_token:  data.access_token,
      refresh_token: data.refresh_token ?? null,
      token_type:    data.token_type    ?? 'Bearer',
      expires_in:    data.expires_in    ?? null,
    },
    user: data.user ?? null,
    session: data.session ?? null,
  };
}

export function AuthProvider({ children }) {
  const [user,          setUser]          = useState(null);
  const [accessToken,   setAccessToken]   = useState(null);
  const [refreshToken,  setRefreshToken]  = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);

  // MFA challenge state — staged after /auth/login returns mfa_required.
  // Cleared on completion, cancel, or expiry.
  const [mfaPending, setMfaPending] = useState(null);   // { token, methods, expiresIn, rememberMe }

  // ── Helpers ───────────────────────────────────────────────────────────────
  const storeAuth = useCallback((auth, userData, session) => {
    saveAuthSession(auth, userData, session);
    setAccessToken(auth.access_token);
    setRefreshToken(auth.refresh_token);
    setUser(userData);
    setMfaPending(null);
    // Rule 48 — tag Sentry scope with the authenticated user.
    setSentryUser(userData);
  }, []);

  /**
   * Architecture Rule 64 — finalize a login from any auth-completing endpoint
   * (/auth/login OR /auth/mfa/challenge). Both MUST return the same envelope
   * including `user`. As defense-in-depth, if `user` is absent we hydrate it
   * from /auth/me before storing — so a token-only envelope can never leave
   * the SPA "logged in but user === null", which silently bounces the route
   * guard back to /login.
   */
  const finalizeAuth = useCallback(async (data) => {
    const { auth, user: userData, session } = readTokenEnvelope(data);
    let resolvedUser = userData;
    if (auth.access_token) {
      try {
        const meRes = await meRequest(auth.access_token);
        resolvedUser = meRes.data.data ?? resolvedUser;
      } catch (err) {
        if (!resolvedUser) throw err;
      }
    }
    storeAuth(auth, resolvedUser, session);
  }, [storeAuth]);

  const clearAuth = useCallback(() => {
    clearAuthSession();
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    setMfaPending(null);
    // Rule 48 — anonymous events from here on.
    clearSentryUser();
  }, []);

  // ── Initialize ────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    async function initializeAuth() {
      const storedToken   = getAuthToken();
      const storedRefresh = getRefreshToken();

      if (!storedToken && !storedRefresh) {
        setIsLoadingAuth(false);
        return;
      }

      try {
        if (storedToken) {
          const res = await meRequest(storedToken);
          if (!cancelled) {
            setAccessToken(storedToken);
            setRefreshToken(storedRefresh);
            setUser(res.data.data);
            setSentryUser(res.data.data);
          }
          return;
        }
      } catch {
        // Access token invalid — try refresh below
      }

      try {
        if (storedRefresh) {
          const refreshRes = await refreshRequest(storedRefresh);
          const { auth, user: userData, session } = readTokenEnvelope(refreshRes.data.data);
          saveAuthSession(auth, userData, session);

          const meRes = await meRequest(auth.access_token);
          if (!cancelled) {
            setAccessToken(auth.access_token);
            setRefreshToken(auth.refresh_token);
            setUser(meRes.data.data);
            setSentryUser(meRes.data.data);
          }
          return;
        }
      } catch {
        // Both tokens invalid — clear everything
        clearAuthSession();
        clearSentryUser();
      }

      if (!cancelled) {
        setIsLoadingAuth(false);
      }
    }

    initializeAuth().finally(() => {
      if (!cancelled) setIsLoadingAuth(false);
    });

    return () => { cancelled = true; };
  }, []);

  // ── Public actions ────────────────────────────────────────────────────────

  /**
   * Returns one of:
   *   { status: 'success' }                                — context updated, session live
   *   { status: 'mfa_required', methods, expiresIn }       — challenge staged; call submitMfaChallenge()
   */
  const login = useCallback(async (email, password, rememberMe = false) => {
    const res  = await loginRequest(email, password, rememberMe);
    const data = res.data?.data ?? {};

    if (data.mfa_required) {
      setMfaPending({
        token:     data.mfa_challenge_token,
        methods:   data.mfa_methods   ?? ['totp', 'backup_code'],
        expiresIn: data.expires_in    ?? 300,
        rememberMe: Boolean(rememberMe),
        receivedAt: Date.now(),
      });
      return {
        status:    'mfa_required',
        methods:   data.mfa_methods   ?? ['totp', 'backup_code'],
        expiresIn: data.expires_in    ?? 300,
      };
    }

    await finalizeAuth(data);
    return { status: 'success' };
  }, [finalizeAuth]);

  /**
   * Complete an MFA-gated login by consuming the staged challenge token.
   *
   * Throws if no challenge is staged. Caller MUST check `mfaPending` is truthy
   * before invoking. On a wrong code the backend returns 401 → caller surfaces
   * the error and the challenge stays staged for retry until it expires.
   */
  const submitMfaChallenge = useCallback(async (code, codeType = 'totp') => {
    if (!mfaPending?.token) {
      throw new Error('No MFA challenge is pending. Sign in again.');
    }
    const res  = await mfaChallengeRequest(
      mfaPending.token,
      code,
      codeType,
      Boolean(mfaPending.rememberMe),
    );
    const data = res.data?.data ?? {};
    await finalizeAuth(data);
    return { status: 'success' };
  }, [mfaPending, finalizeAuth]);

  const cancelMfaChallenge = useCallback(() => {
    setMfaPending(null);
  }, []);

  const logout = useCallback(async () => {
    try {
      if (accessToken) await logoutRequest(accessToken);
    } finally {
      clearAuth();
    }
  }, [accessToken, clearAuth]);

  /** Re-fetch `/auth/me` and update the cached `user`. Self-service pages
      that mutate user state (MFA enable/disable, password change, etc.)
      should call this so the indicator/badge stays in sync without a
      full sign-out. Safe no-op if no token is loaded. */
  const refreshUser = useCallback(async () => {
    if (!accessToken) return;
    try {
      const res = await meRequest(accessToken);
      if (res?.data?.data) {
        setUser(res.data.data);
        setSentryUser(res.data.data);
      }
    } catch {
      // ignore — the next 401 will trigger logout via the axios interceptor
    }
  }, [accessToken]);

  // ── Context value (stable reference via useMemo) ─────────────────────────
  const value = useMemo(() => ({
    user,
    accessToken,
    refreshToken,
    isLoadingAuth,
    isAuthenticated: !!user,
    mfaPending,
    login,
    submitMfaChallenge,
    cancelMfaChallenge,
    logout,
    refreshUser,
    // Rule 70 — OAuthCallbackPage exchanges the handoff code for the same
    // Rule-64 envelope the rest of the auth flow produces, then hands it to
    // finalizeAuth so the SPA enters the authenticated state via one path.
    finalizeAuth,
  }), [user, accessToken, refreshToken, isLoadingAuth, mfaPending, login, submitMfaChallenge, cancelMfaChallenge, logout, refreshUser, finalizeAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
